/* eslint-env browser */
import 'core-js';
import 'regenerator-runtime/runtime';

import _ from 'lodash';
import Url from 'url';
import React from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import * as Sentry from '@sentry/browser';
import * as SentryIntegrations from '@sentry/integrations';
import ReactGA from 'react-ga';
import { HelmetProvider } from 'react-helmet-async';

import { Flex, Box } from 'reflexbox';

import { NativeEventSource, EventSourcePolyfill } from 'event-source-polyfill';

import config from 'frontend/config';
import { potions, foods, flasks } from 'shared/simcOptions';
import { getSpecInfo, getSpecInfoById } from 'shared/character';
import { getEnhancementsForSlotNameAndSpec, gemInfo } from 'shared/items';
import { isBetaHost } from 'shared/util';
import 'shared/axiosRetry';

import { retry, dlog, derror } from 'frontend/util';

import SimcVersionSingleton from 'frontend/simcVersion';

import { RaidbotsSkull, DefaultErrorBoundary } from 'frontend/components';

import createStore from './state/store';
import actions from './state/actions';
import rootReducer from './state/reducers';

// polyfill EventSource for Edge and a few other browsers
window.EventSource = NativeEventSource || EventSourcePolyfill;

Sentry.init({
  dsn: 'https://7b5eb09d382745929edd40ca028764bf@sentry.io/123015',
  environment: process.env.NODE_ENV || 'development',
  release: `frontend-${process.env.VERSION}`,
  integrations: [
    new SentryIntegrations.ExtraErrorData(),
  ],
  whitelistUrls: [
    /https?:\/\/((ulduar|beta|www|mimiron)\.)?raidbots\.com/,
  ],
  ignoreErrors: [
    // google translate extension error
    'a[b].target.className.indexOf is not a function',

    // misc errors that are probably extensions, weird browser versions, or non-fatal edge cases
    'ResizeObserver loop limit exceeded',
    'ResizeObserver loop completed with undelivered notifications.',
    'NotFoundError: Failed to execute \'removeChild\' on \'Node\': The node to be removed is not a child of this node.',
    'Unexpected end of input',
    'hideMyLocation',
    'Unexpected token \'else\'',
    'Unexpected end of input',
    'The node before which the new node is to be inserted is not a child of this node.',
    'The node to be removed is not a child of this node',
    'can\'t redefine non-configurable property "userAgent"',
    'Should not already be working.',
    'adb_rotate?', // some .ru domain XHR, probably from an extension
    'undefined is not an object (evaluating \'webkit.messageHandlers.loginsManagerMessageHandler.postMessage\')',
    // microsoft crawler triggering a recursive error?
    'Non-Error promise rejection captured with value: Object Not Found Matching Id',
  ],
});

const url = Url.parse(window.location.href, true);
const headless = Boolean(url.query.headless);

if (process.env.NODE_ENV === 'production') {
  // use testMode to avoid tracking puppeteer requests
  ReactGA.initialize('UA-20834829-1', { testMode: headless });
} else {
  ReactGA.initialize('testing', { testMode: true });
}


// local storage

let initialState;
if (window.initialData) {
  // get default reducer keys
  const defaultData = rootReducer();

  initialState = {
    ...window.initialData,
    // remove unknown user fields before putting into the reducer
    user: _.pick(window.initialData.user, _.keys(defaultData.user)),
  };

  const user = _.get(initialState, 'user');
  if (user && user.key) {
    Sentry.setUser({
      id: user.key,
      username: user.username,
      email: user.email,
    });
  }
}
// isStorageAvailable sources from MDN on 2019-08-22
// eslint-disable-next-line
// https://developer.mozilla.org/en-US/docs/Web/API/Web_Storage_API/Using_the_Web_Storage_API#Feature-detecting_localStorage
const isStorageAvailable = type => {
  let storage;
  try {
    storage = window[type];
    const key = '__raidbots_storage_test__';
    storage.setItem(key, key);
    storage.removeItem(key);
    return true;
  } catch (e) {
    return e instanceof DOMException && (
      e.code === 22 ||
      e.code === 1014 ||
      e.name === 'QuotaExceededError' ||
      e.name === 'NS_ERROR_DOM_QUOTA_REACHED') &&
      (storage && storage.length !== 0);
  }
};

if (isStorageAvailable('localStorage')) {
  try {
    if (window.localStorage && window.localStorage.getItem) {
      const localStore = window.localStorage.getItem('store');
      if (localStore) {
        const localData = JSON.parse(localStore);

        const stateVersion = _.get(localData, 'version.stateVersion');

        if (!stateVersion) {
          // migrate old jobs data to an archive
          if (_.get(localData, 'simbot.jobs')) {
            localData.simbot.archivedJobs = localData.simbot.jobs;
            delete localData.simbot.jobs;
          }
          // migrate old stable simc version to new weekly.  stable is now called release
          if (_.get(localData, 'simbot.simcOptions.simcVersion') === 'stable') {
            localData.simbot.simcOptions.simcVersion = 'weekly';
          }
          // clear out old email field
          if (_.get(localData, 'simbot.form.email')) {
            delete localData.simbot.form.email;
          }
          // migrate 1000 iterations to 10000 iterations
          if (_.get(localData, 'simbot.simcOptions.iterations') === 1000) {
            localData.simbot.simcOptions.iterations = 10000;
          }
          // migrate release simc version to weekly.  release is gone
          if (_.get(localData, 'simbot.simcOptions.simcVersion') === 'release') {
            localData.simbot.simcOptions.simcVersion = 'weekly';
          }
          if (_.get(localData, 'simbot.simcOptions.iterations')) {
            localData.simbot.simcOptions.iterations = 'smart';
          }
          _.set(localData, 'version.stateVersion', 1);
          window.localStorage.setItem('store', JSON.stringify(localData));
        }

        if (stateVersion <= 1) {
          // BfA data resets
          _.set(localData, 'simbot.optimize', {
            ..._.get(localData, 'simbot.optimize'),
            requireLegendaries: false,
            gemEnchant: {
              gem: [],
              cloakEnchant: [],
              neckEnchant: [],
              ringEnchant: [],
              weaponEnchant: [],
              replaceExisting: false,
            },
          });
          _.set(localData, 'simbot.simcOptions', {
            ..._.get(localData, 'simbot.simcOptions'),
            augmentation: '',
            flask: '',
            food: '',
            potion: '',
            ptr: false,
          });
          _.set(localData, 'simbot.customApls', {
            ..._.get(localData, 'simbot.customApls'),
            customApls: [],
          });
          _.set(localData, 'site.overlay', 'bfa-notes');
          _.set(localData, 'version.stateVersion', 2);
          window.localStorage.setItem('store', JSON.stringify(localData));
        }

        if (stateVersion <= 2) {
          // copy current weapon enchant settings to off hand enchant
          const weaponEnchant = _.get(localData, 'simbot.optimize.gemEnchant.weaponEnchant');
          _.set(localData, 'simbot.optimize.gemEnchant.weaponOffhandEnchant', weaponEnchant);

          // reset Pantheon trinket setting
          _.set(localData, 'simbot.simcOptions.pantheonTrinkets', 0);

          // reset PTR setting
          _.set(localData, 'simbot.simcOptions.ptr', false);

          // update state version
          _.set(localData, 'version.stateVersion', 3);
          window.localStorage.setItem('store', JSON.stringify(localData));
        }

        if (stateVersion <= 3) {
          // reset invalid potion
          const potion = _.get(localData, 'simbot.simcOptions.potion');
          const selectedPotion = _.find(potions, { value: potion });
          if (!selectedPotion) {
            _.set(localData, 'simbot.simcOptions.potion', '');
          }

          // reset invalid food
          const food = _.get(localData, 'simbot.simcOptions.food');
          const selectedFood = _.find(foods, { value: food });
          if (!selectedFood) {
            _.set(localData, 'simbot.simcOptions.food', '');
          }

          // reset invalid flask
          const flask = _.get(localData, 'simbot.simcOptions.flask');
          const selectedFlask = _.find(flasks, { value: flask });
          if (!selectedFlask) {
            _.set(localData, 'simbot.simcOptions.flask', '');
          }

          // update state version
          _.set(localData, 'version.stateVersion', 4);
          window.localStorage.setItem('store', JSON.stringify(localData));
        }


        if (stateVersion <= 4) {
          // fix that poor guy who has PTR flag stuck
          _.set(localData, 'simbot.simcOptions.ptr', false);

          // migrate old gem/enchant settings to new data
          const gemEnchant = _.get(localData, 'simbot.optimize.gemEnchant');
          const selectedEnhancements = _.get(localData, 'simbot.optimize.enhancements.selectedEnhancements', []);
          if (gemEnchant && _.size(selectedEnhancements) === 0) {
            let showAll = false;
            const enhancements = [];
            const theMap = {
              gem: 'socket',
              ringEnchant: 'finger',
              weaponEnchant: 'main_hand',
              weaponOffhandEnchant: 'off_hand',
            };
            _.each(theMap, (slot, source) => {
              _.each(gemEnchant[source], setting => {
                if (_.isObject(setting)) {
                  const specInfo = getSpecInfo(setting.className, setting.spec);
                  if (specInfo) {
                    const validEnhancements = getEnhancementsForSlotNameAndSpec(slot, specInfo);
                    const enhancementId = parseInt(setting.value, 10);
                    if (enhancementId) {
                      const validEnhancement = _.find(validEnhancements, enh => {
                        return enhancementId === enh.id || enhancementId === enh.itemId;
                      });
                      if (validEnhancement) {
                        if (!validEnhancement.preferred) {
                          showAll = true;
                        }
                        enhancements.push({
                          specId: specInfo.id,
                          slot,
                          enhancementId,
                        });
                      }
                    }
                  }
                }
              });
            });

            _.set(localData, 'simbot.optimize.enhancements.selectedEnhancements', enhancements);
            _.set(localData, 'simbot.optimize.enhancements.forceMainStat', []);
            if (showAll) {
              _.set(localData, 'simbot.optimize.enhancementAdvancedControls', showAll);
            }
          }

          // fix old bfa-dev testers
          if (_.get(localData, 'simbot.simcOptions.simcVersion') === 'bfa-dev') {
            _.set(localData, 'simbot.simcOptions.simcVersion', 'nightly');
          }

          // update state version
          _.set(localData, 'version.stateVersion', 5);
          window.localStorage.setItem('store', JSON.stringify(localData));
        }

        if (stateVersion <= 5) {
          // remove old offhand enchant settings for specs that can't offhand
          const selectedEnhancements = _.get(localData, 'simbot.optimize.enhancements.selectedEnhancements');
          if (_.size(selectedEnhancements) > 0) {
            const fixedEnhancements = _.filter(selectedEnhancements, enh => {
              if (enh.slot === 'off_hand') {
                const specInfo = getSpecInfoById(enh.specId);
                if (!specInfo.dualWield) {
                  return false;
                }
              }
              return true;
            });
            _.set(localData, 'simbot.optimize.enhancements.selectedEnhancements', fixedEnhancements);
          }

          // update state version
          _.set(localData, 'version.stateVersion', 6);
          window.localStorage.setItem('store', JSON.stringify(localData));
        }

        if (stateVersion <= 6) {
          // force RA stacks to 0
          _.set(localData, 'simbot.simcOptions.reoriginationArray', 0);

          // update state version
          _.set(localData, 'version.stateVersion', 7);
          window.localStorage.setItem('store', JSON.stringify(localData));
        }

        if (stateVersion <= 7) {
          // disable zuldazar
          _.set(localData, 'simbot.simcOptions.zuldazar', false);

          // update state version
          _.set(localData, 'version.stateVersion', 8);
          window.localStorage.setItem('store', JSON.stringify(localData));
        }

        if (stateVersion <= 8) {
          // remove punchcards from enhancements
          const punchcardIds = _.map(_.filter(
            gemInfo,
            g => _.includes(['RED_PUNCHCARD', 'YELLOW_PUNCHCARD', 'BLUE_PUNCHCARD'], g.socket),
          ), 'id');
          _.set(localData, 'simbot.optimize.enhancements.selectedEnhancements', _.reject(
            _.get(localData, 'simbot.optimize.enhancements.selectedEnhancements'),
            enhancement => _.includes(punchcardIds, enhancement.enhancementId),
          ));

          // update state version
          _.set(localData, 'version.stateVersion', 9);
          window.localStorage.setItem('store', JSON.stringify(localData));
        }

        if (stateVersion <= 9) {
          // I allowed higher values than SimC expects
          const alliesPath = 'simbot.simcOptions.worldveinAllies';
          if (_.get(localData, alliesPath) > 10) {
            _.set(localData, alliesPath, 10);
          }

          // update state version
          _.set(localData, 'version.stateVersion', 10);
          window.localStorage.setItem('store', JSON.stringify(localData));
        }

        if (stateVersion <= 10) {
          // delete broken talent overrides that contain NaNs
          const talentOverridePath = 'simbot.character.talents';
          const overrides = _.get(localData, talentOverridePath);
          if (overrides && overrides.match(/NaN/)) {
            _.set(localData, talentOverridePath, null);
          }

          // update state version
          _.set(localData, 'version.stateVersion', 11);
          window.localStorage.setItem('store', JSON.stringify(localData));
        }

        if (stateVersion <= 11) {
          // force sim expiration / privacy policy overlay
          _.set(localData, 'site.overlay', 'sim-expiration');

          // update state version
          _.set(localData, 'version.stateVersion', 12);
          window.localStorage.setItem('store', JSON.stringify(localData));
        }

        if (stateVersion <= 12) {
          // set detailed progress as default
          _.set(localData, 'report.detailedProgress', true);

          // update state version
          _.set(localData, 'version.stateVersion', 13);
          window.localStorage.setItem('store', JSON.stringify(localData));
        }

        if (stateVersion <= 13) {
          // sigh, actually set the correct value
          _.set(localData, 'simbot.report.detailedProgress', true);

          // update state version
          _.set(localData, 'version.stateVersion', 14);
          window.localStorage.setItem('store', JSON.stringify(localData));
        }

        if (stateVersion <= 14) {
          // disable 8.2 options
          _.set(localData, 'simbot.simcOptions.nazjatar', false);
          _.set(localData, 'simbot.simcOptions.aberration', false);

          // set this back to 0 - I think group-based toggles like this should probably be low/0 by default
          _.set(localData, 'simbot.simcOptions.worldveinAllies', 0);

          // nudge users back to smart sim
          _.set(localData, 'simbot.simcOptions.iterations', 'smart');

          // update state version
          _.set(localData, 'version.stateVersion', 15);
          window.localStorage.setItem('store', JSON.stringify(localData));
        }

        if (stateVersion <= 15) {
          // remove warforgeLevel in droptimizer M+ instanceOptions as these no longer WF/TF in 8.3
          const mythicOptionsIdx = _.findIndex(
            _.get(localData, 'simbot.droptimizer.instanceOptions'), { instanceId: -1 },
          );
          if (mythicOptionsIdx > -1) {
            localData.simbot.droptimizer.instanceOptions[mythicOptionsIdx].warforgeLevel = {};
          }

          // update state version
          _.set(localData, 'version.stateVersion', 16);
          window.localStorage.setItem('store', JSON.stringify(localData));
        }

        if (stateVersion <= 16) {
          // update surging vitality values
          const svMap = {
            35: 12,
            26: 8,
            21: 6,
            16: 5,
            13: 4,
            5: 3,
          };
          const oldSurgingValue = _.get(localData, 'simbot.simcOptions.surgingVitality');
          if (oldSurgingValue) {
            _.set(localData, 'simbot.simcOptions.surgingVitality', svMap[oldSurgingValue] || 0);
          }
          // update state version
          _.set(localData, 'version.stateVersion', 17);
          window.localStorage.setItem('store', JSON.stringify(localData));
        }

        if (stateVersion <= 17) {
          // Shadowlands data resets

          _.set(localData, 'simbot.optimize.enhancements.selectedEnhancements', []);
          _.set(localData, 'simbot.optimize.enhancements.forceMainStat', []);
          _.set(localData, 'simbot.optimize.enhancements.wrathionSocket', []);

          _.set(localData, 'simbot.droptimizer.selectedInstance', null);
          _.set(localData, 'simbot.droptimizer.instanceOptions', []);
          _.set(localData, 'simbot.droptimizer.instances', []);
          _.set(localData, 'simbot.droptimizer.encounters', []);
          _.set(localData, 'simbot.droptimizer.difficulty', 'raid-normal');
          _.set(localData, 'simbot.droptimizer.allAzeritePowers', false);

          _.set(localData, 'simbot.simcOptions.iterations', 'smart');
          _.set(localData, 'simbot.simcOptions.potion', '');
          _.set(localData, 'simbot.simcOptions.food', '');
          _.set(localData, 'simbot.simcOptions.flask', '');
          _.set(localData, 'simbot.simcOptions.augmentation', '');

          _.set(localData, 'site.overlay', 'sl-notes');
          _.set(localData, 'version.stateVersion', 18);
        }

        if (stateVersion <= 18) {
          // clear APLs, should have done this at SL launch
          _.set(localData, 'simbot.customApls.customApls', []);

          _.set(localData, 'version.stateVersion', 19);
        }

        if (stateVersion <= 19) {
          // Reduce max conduit rank override to current max obtainable rank in-game
          const croPath = 'simbot.optimize.covenants.conduitRankOverride';
          if (_.get(localData, croPath) > 9) {
            _.set(localData, croPath, 9);
          }

          _.set(localData, 'version.stateVersion', 20);
        }

        if (stateVersion <= 20) {
          // fix titanic gland default, got deployed at 0 accidentally
          const ocularGlandPath = 'simbot.simcOptions.ocularGlandUptime';
          if (_.get(localData, ocularGlandPath) === 0) {
            _.set(localData, ocularGlandPath, 100);
          }

          _.set(localData, 'version.stateVersion', 21);
        }

        initialState = { ...initialState, ...localData };
      }
    }
  } catch (ex) {
    // can't use local storage for some reason
    derror('Unable to load store from localStorage, settings will not be saved across sessions');

    Sentry.captureException(ex);
  }
}

if (url.query.region) {
  _.set(initialState, 'simbot.form.armoryRegion', url.query.region.toLowerCase());
  _.set(initialState, 'simbot.character.data', null);
}
if (url.query.realm) {
  _.set(initialState, 'simbot.form.armoryRealm', url.query.realm.toLowerCase());
  _.set(initialState, 'simbot.character.data', null);
}
if (url.query.name) {
  _.set(initialState, 'simbot.form.armoryName', url.query.name);
  _.set(initialState, 'simbot.character.data', null);
}

SimcVersionSingleton.set(_.get(initialState, 'simbot.simcOptions.simcVersion', 'weekly'));

let store;

// status interval
// do not run if using a custom/beta build
if (url.query.build) {
  dlog('Custom build, do not run status interval');
  _.set(initialState, 'site.customBuild', url.query.build);
} else if (isBetaHost(_.get(window, 'location.hostname'))) {
  dlog('Beta build, do not run status interval');
  _.set(initialState, 'site.customBuild', _.get(window.initialData, 'version.initialVersion'));
} else {
  // run every 5 minutes + (0-30) seconds
  const interval = (5 * 60 * 1000) + (1000 * _.random(0, 32));
  setInterval(() => actions.fetchStatus()(store.dispatch), interval);
}

store = createStore(initialState);

class BootScreen extends React.Component {
  state = {
    show: false,
  }

  componentDidMount() {
    this.timeout = setTimeout(() => {
      this.setState({ show: true });
    }, 300);
  }

  componentWillUnmount() {
    clearTimeout(this.timeout);
  }

  render() {
    if (!this.state.show) {
      return null;
    }
    return (
      <Flex justify="center" align="center" column style={{ height: '100%' }}>
        <Box>
          <RaidbotsSkull fill="#444" />
          <Box
            style={{
              color: '#555',
              fontFamily: config.fontFamily,
              textAlign: 'center',
            }}
          >
            Loading...
          </Box>
        </Box>
      </Flex>
    );
  }
}

const RaidbotsRouter = React.lazy(() => retry(() => import('./components/Router')));
const LazyApp = () => (
  <DefaultErrorBoundary>
    <React.Suspense fallback={<BootScreen />}>
      <RaidbotsRouter />
    </React.Suspense>
  </DefaultErrorBoundary>
);

const App = LazyApp;

ReactDOM.render(
  <HelmetProvider>
    <Provider store={store}>
      <App />
    </Provider>
  </HelmetProvider>,
  document.getElementById('app'),
);

/*
if (module.hot) {
  module.hot.accept('./components/Router', () => {
    const HotApp = hot(module)(require('./components/Router').default); // eslint-disable-line global-require
    ReactDOM.render(
      <Provider store={store}>
        <HotApp />
      </Provider>,
      document.getElementById('app'),
    );
  });
}
*/

window.raidbots = {
  version: process.env.VERSION,
  env: process.env.NODE_ENV,
};
